import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import GlobalSidebar from '../components/GlobalSidebar';
import { GlobalButton } from '../components/GlobalButton';
import GlobalEmptyState from '../components/GlobalEmptyState';
import { StatCard } from '../components/StatCard';
import { SortableStatGrid } from '../components/SortableStatGrid';
import { ViewPage, ViewSectionHeader } from '../components/ViewPage';
import { ViewLayoutToggle } from '../components/ViewLayoutToggle';
import { ViewSections, type ViewSection } from '../components/ViewSections';
import { useViewLayoutMode } from '../hooks/useViewLayoutMode';
import { InvestmentCompanyForm } from '../components/InvestmentCompanyForm';
import { CompanyUpdateForm } from '../components/CompanyUpdateForm';
import { CompanyUpdateTimeline } from '../components/CompanyUpdateTimeline';
import {
  FounderUpdateIntelligenceSections,
  FounderUpdateSignalPills,
} from '../components/FounderUpdateIntelligenceSections';
import {
  EntityDocumentsPanel,
  type EntityDocumentUploadControl,
} from '../components/EntityDocumentsPanel';
import { InvestmentChatPanel } from '../components/InvestmentChatPanel';
import { MonthlyUpdateForm } from '../components/MonthlyUpdateForm';
import { MonthlyMetricsChart, hasMonthlyMetricsChartData } from '../components/MonthlyMetricsChart';
import { useAnalysisJobs } from '../context/AnalysisJobsContext';
import { useAppData } from '../context/AppDataContext';
import { useUser } from '../context/UserContext';
import { useTheme } from '../context/ThemeProvider';
import { deleteMonthlyUpdatePdf } from '../lib/monthlyUpdatePdfStorage';
import { computeCompanyMetrics } from '../utils/calculations';
import {
  computeRunwayMonths,
  formatDateLong,
  formatEuro,
  formatEuroOptional,
  formatHoldingPeriod,
  formatMonthYear,
  formatMultiple,
  formatPercentPoints,
  formatRunway,
  formatSignedEuro,
} from '../utils/formatters';
import { Favicon, normalizeWebsiteUrl } from '../utils/favicon';
import { getInvestmentStatus } from '../utils/investmentCompanyUi';
import { useFounderUpdateInboxItems } from '../hooks/useFounderUpdateInboxItems';
import { founderUpdateSourceFingerprint } from '../utils/founderUpdateIntelligence';
import type { CompanyUpdate, InvestmentCompany, MonthlyUpdate } from '../types';
import styles from './InvestmentCompanyDetailView.module.css';
import { newId } from '../utils/id';

type SidebarMode = 'closed' | 'editCompany' | 'addReport' | 'editReport' | 'addUpdate' | 'editUpdate';

function signedValueClass(value: number): string {
  if (value > 0) return 'financialSnapshot-profit financialSnapshot-profit--positive';
  if (value < 0) return 'financialSnapshot-profit financialSnapshot-profit--negative';
  return '';
}

function assetValueNode(value: number): ReactNode {
  return <span className={signedValueClass(value)}>{formatSignedEuro(value)}</span>;
}

function investedValueNode(value: number): ReactNode {
  const amount = Math.abs(value);
  if (amount === 0) return formatEuro(0);
  return (
    <span className="financialSnapshot-profit financialSnapshot-profit--negative">
      −{formatEuro(amount, true)}
    </span>
  );
}

function moicValueNode(moic: number | null): ReactNode {
  if (moic == null) return '—';
  const formatted = formatMultiple(moic);
  if (moic > 1) {
    return <span className="financialSnapshot-profit financialSnapshot-profit--positive">{formatted}</span>;
  }
  if (moic < 1) {
    return <span className="financialSnapshot-profit financialSnapshot-profit--negative">{formatted}</span>;
  }
  return formatted;
}

function investmentStatusHint(status: ReturnType<typeof getInvestmentStatus>): string {
  switch (status) {
    case 'exit':
      return 'Exited — live marks replaced by proceeds when set';
    case 'dead':
      return 'Written off — valued at zero';
    default:
      return 'Active on the cap table';
  }
}

function monthlyProfitLabel(revenue?: number, burn?: number): string {
  if (
    revenue === undefined ||
    burn === undefined ||
    !Number.isFinite(revenue) ||
    !Number.isFinite(burn)
  ) {
    return '—';
  }
  return formatSignedEuro(revenue - burn);
}

const MIN_ANALYZE_CHARS = 200;

function reportHasAnalyzableSource(report: MonthlyUpdate): boolean {
  const raw = report.rawText?.trim() ?? '';
  return raw.length > 0 || Boolean(report.pdfStoragePath);
}

function reportCanAnalyze(report: MonthlyUpdate): boolean {
  const raw = report.rawText?.trim() ?? '';
  if (raw.length >= MIN_ANALYZE_CHARS) return true;
  return Boolean(report.pdfStoragePath) && raw.length === 0;
}

function reportSourceFingerprint(report: MonthlyUpdate): string {
  const raw = report.rawText?.trim() ?? '';
  return raw ? founderUpdateSourceFingerprint(raw) : '';
}

function reportAnalysisIsStale(report: MonthlyUpdate): boolean {
  const intelligence = report.updateIntelligence;
  const fingerprint = reportSourceFingerprint(report);
  return Boolean(intelligence && fingerprint && intelligence.sourceTextFingerprint !== fingerprint);
}

function monthlyProfitClassName(revenue?: number, burn?: number): string | undefined {
  if (
    revenue === undefined ||
    burn === undefined ||
    !Number.isFinite(revenue) ||
    !Number.isFinite(burn)
  ) {
    return undefined;
  }
  const p = revenue - burn;
  if (p > 0) return 'financialSnapshot-profit financialSnapshot-profit--positive';
  if (p < 0) return 'financialSnapshot-profit financialSnapshot-profit--negative';
  return undefined;
}

export function InvestmentCompanyDetailView() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const userId = user?.uid ?? '';
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { mode: layoutMode, toggle: toggleLayout } = useViewLayoutMode();
  const { companies, setCompanies, updates, setUpdates, monthlyUpdates, setMonthlyUpdates } = useAppData();
  const { unmatchedItems: unmatchedInboxItems } = useFounderUpdateInboxItems(userId);
  const {
    isAnalyzing,
    getJobError,
    startFounderUpdateAnalysis,
  } = useAnalysisJobs();

  const [sidebar, setSidebar] = useState<SidebarMode>('closed');
  const [editingReport, setEditingReport] = useState<MonthlyUpdate | null>(null);
  const [editingUpdate, setEditingUpdate] = useState<CompanyUpdate | null>(null);
  /** Expanded bodies for monthly report cards (Reports tab). */
  const [expandedReportIds, setExpandedReportIds] = useState<Record<string, boolean>>({});
  const [documentUploadControl, setDocumentUploadControl] =
    useState<EntityDocumentUploadControl | null>(null);

  const company = useMemo(
    () => companies.find((c) => c.id === companyId),
    [companies, companyId]
  );

  const companyReports = useMemo(() => {
    if (!companyId) return [];
    return monthlyUpdates
      .filter((r) => r.companyId === companyId)
      .sort((a, b) => {
        const ta = a.periodMonth ? new Date(a.periodMonth).getTime() : 0;
        const tb = b.periodMonth ? new Date(b.periodMonth).getTime() : 0;
        if (tb !== ta) return tb - ta;
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      });
  }, [monthlyUpdates, companyId]);

  if (!companyId || !company) {
    return (
      <ViewPage title="Investment" backTo="/investments" backLabel="Back to investments">
        <section>
          <p className="muted">Company not found.</p>
        </section>
      </ViewPage>
    );
  }

  const firm = company;

  const m = computeCompanyMetrics(firm);
  const investmentStatus = getInvestmentStatus(firm);
  const ticketSize = Number.isFinite(firm.ticketSize) ? firm.ticketSize : 0;
  let currentValue = m.myEquityValue;
  if (investmentStatus === 'dead') {
    currentValue = 0;
  } else if (investmentStatus === 'exit') {
    currentValue =
      firm.exitTotalEuros != null && Number.isFinite(firm.exitTotalEuros)
        ? firm.exitTotalEuros
        : m.myEquityValue;
  }
  const companyGain = currentValue - ticketSize;
  const companyMoic = ticketSize > 0 ? currentValue / ticketSize : null;
  const industryHint = [
    firm.foundingYear ? `Founded ${firm.foundingYear}` : null,
    firm.countryCode?.trim() ? firm.countryCode.trim().toUpperCase() : null,
  ]
    .filter(Boolean)
    .join(' · ') || 'Sector & market focus';
  const expectedReturnValue =
    investmentStatus === 'exit'
      ? firm.exitTotalEuros != null && Number.isFinite(firm.exitTotalEuros)
        ? assetValueNode(firm.exitTotalEuros)
        : '—'
      : assetValueNode(m.expectedReturnThisYear);
  const expectedReturnHint =
    investmentStatus === 'exit'
      ? 'Realised exit proceeds'
      : firm.expectedDividendThisYear > 0
        ? `Includes ${formatEuro(firm.expectedDividendThisYear, true)} dividends modelled`
        : 'Exit probability × equity plus dividends';
  const websiteHref = normalizeWebsiteUrl(firm.websiteUrl);
  let websiteHost: string | null = null;
  if (websiteHref) {
    try {
      websiteHost = new URL(websiteHref).hostname;
    } catch {
      websiteHost = null;
    }
  }

  function closeSidebar() {
    setSidebar('closed');
  }

  function closeReportSidebar() {
    setSidebar('closed');
    setEditingReport(null);
  }

  function closeUpdateSidebar() {
    setSidebar('closed');
    setEditingUpdate(null);
  }

  function saveCompanyUpdate(next: CompanyUpdate) {
    setUpdates((prev) => {
      const withId = next.id?.trim() ? next : { ...next, id: newId() };
      const idx = prev.findIndex((p) => p.id === withId.id);
      if (idx === -1) return [...prev, withId];
      const copy = [...prev];
      copy[idx] = withId;
      return copy;
    });
    closeUpdateSidebar();
  }

  function deleteCompanyUpdate(row: CompanyUpdate) {
    setUpdates((prev) => prev.filter((p) => p.id !== row.id));
    closeUpdateSidebar();
  }

  function openManualUpdateSidebar() {
    setEditingUpdate(null);
    setSidebar('addUpdate');
  }

  function openAddReportSidebar() {
    setEditingReport(null);
    setSidebar('addReport');
  }

  function saveCompany(next: InvestmentCompany) {
    const id = next.id?.trim() ? next.id : newId();
    const merged = { ...next, id };
    setCompanies((prev) => {
      const idx = prev.findIndex((p) => p.id === id);
      if (idx === -1) return [...prev, merged];
      const copy = [...prev];
      copy[idx] = merged;
      return copy;
    });
    closeSidebar();
  }

  function deleteCompany() {
    setCompanies((prev) => prev.filter((c) => c.id !== firm.id));
    setUpdates((prev) => prev.filter((u) => u.companyId !== firm.id));
    setMonthlyUpdates((prev) => prev.filter((r) => r.companyId !== firm.id));
    navigate('/investments');
  }

  function saveMonthlyReport(next: MonthlyUpdate) {
    setMonthlyUpdates((prev) => {
      const idx = prev.findIndex((p) => p.id === next.id);
      if (idx === -1) return [...prev, next];
      const copy = [...prev];
      copy[idx] = next;
      return copy;
    });
    closeReportSidebar();
  }

  function deleteMonthlyReport(row: MonthlyUpdate) {
    void deleteMonthlyUpdatePdf(row.pdfStoragePath);
    setMonthlyUpdates((prev) => prev.filter((p) => p.id !== row.id));
    setExpandedReportIds((prev) => {
      const { [row.id]: _, ...rest } = prev;
      return rest;
    });
    closeReportSidebar();
  }

  function toggleReportExpanded(id: string) {
    setExpandedReportIds((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  function runFounderUpdateAnalysis(report: MonthlyUpdate) {
    if (!reportCanAnalyze(report)) return;
    void startFounderUpdateAnalysis(report.id);
  }

  const detailsSection = (
    <>
      <ViewSectionHeader eyebrow="Investment" title={firm.name} />

      <SortableStatGrid
        viewId="investments-detail"
        className="grid-stats grid-stats-2"
        ariaLabel="Investment summary"
        cards={[
          {
            id: 'stake',
            pinned: true,
            label: 'Your stake',
            node: (
              <StatCard
                emphasis
                label="Your stake"
                emoji="💎"
                value={assetValueNode(currentValue)}
                hint={`${formatPercentPoints(firm.ownershipPercentage)} of EV · ${investmentStatusHint(investmentStatus)}`}
                valueWrap
              />
            ),
          },
          {
            id: 'return',
            label: 'Return',
            node: (
              <StatCard
                label="Return"
                emoji="📊"
                value={assetValueNode(companyGain)}
                hint={
                  ticketSize > 0
                    ? `${formatPercentPoints((companyGain / ticketSize) * 100)} vs ticket paid`
                    : 'Add ticket size to benchmark gain'
                }
                valueWrap
              />
            ),
          },
          {
            id: 'moic',
            label: 'MOIC',
            node: (
              <StatCard
                label="MOIC"
                emoji="🧮"
                value={moicValueNode(companyMoic)}
                hint={
                  ticketSize > 0
                    ? 'Current value ÷ capital deployed'
                    : 'Add ticket size to track multiple on capital'
                }
              />
            ),
          },
          {
            id: 'ticket',
            label: 'Ticket size',
            node: (
              <StatCard
                label="Ticket size"
                emoji="💳"
                value={investedValueNode(ticketSize)}
                hint={
                  firm.investmentDate
                    ? `Invested ${formatDateLong(firm.investmentDate)}`
                    : 'Capital deployed in this round'
                }
                valueWrap
              />
            ),
          },
          {
            id: 'industry',
            label: 'Industry',
            node: (
              <StatCard
                label="Industry"
                emoji="🏷️"
                value={firm.industry || '—'}
                hint={industryHint}
                valueWrap
              />
            ),
          },
          {
            id: 'website',
            label: 'Website',
            node: (
              <StatCard
                label="Website"
                emoji="🌐"
                value={
                  websiteHref && websiteHost ? (
                    <a
                      href={websiteHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="detailWebsiteLink"
                    >
                      {websiteHost}
                    </a>
                  ) : (
                    '—'
                  )
                }
                hint={websiteHref ? 'Opens company site in a new tab' : 'Add URL when editing company'}
                valueWrap
              />
            ),
          },
          {
            id: 'valuation',
            label: 'Company valuation',
            node: (
              <StatCard
                label="Company valuation"
                emoji={firm.websiteUrl ? undefined : '📈'}
                value={
                  <>
                    {firm.websiteUrl ? (
                      <Favicon
                        websiteUrl={firm.websiteUrl}
                        size={22}
                        className="companyFavicon"
                        alt=""
                      />
                    ) : null}
                    {assetValueNode(m.companyValuation)}
                  </>
                }
                hint={`Headline EV · your stake is ${formatEuro(m.myEquityValue, true)}`}
                valueWrap
              />
            ),
          },
          {
            id: 'exit-probability',
            label: 'Exit probability',
            node: (
              <StatCard
                label="Exit probability"
                emoji="🎲"
                value={formatPercentPoints(firm.exitProbabilityThisYear)}
                hint={`${formatEuro(m.expectedExitValueThisYear, true)} expected exit value this year`}
              />
            ),
          },
          {
            id: 'expected-return',
            label: 'Expected return',
            node: (
              <StatCard
                label="Expected return"
                emoji="💰"
                value={expectedReturnValue}
                hint={expectedReturnHint}
                valueWrap
              />
            ),
          },
          {
            id: 'holding-period',
            label: 'Holding period',
            node: (
              <StatCard
                label="Holding period"
                emoji="⏱️"
                value={formatHoldingPeriod(firm.investmentDate)}
                hint={
                  firm.statusDate && investmentStatus !== 'active'
                    ? `${investmentStatus === 'exit' ? 'Exited' : 'Written off'} ${formatDateLong(firm.statusDate)}`
                    : 'Time since original investment'
                }
              />
            ),
          },
        ]}
      />
    </>
  );

  const trendSection = (
    <>
      <ViewSectionHeader
        eyebrow="Investment"
        title="Trend"
        subtitle="Sentiment, exit probability and company health (1–6) per report month, plotted from founding."
      />
      <section className="monthlyChartSection" aria-label="Monthly ratings over time">
        {hasMonthlyMetricsChartData(companyReports, firm.foundingYear) ? (
          <MonthlyMetricsChart reports={companyReports} foundingYear={firm.foundingYear} />
        ) : (
          <GlobalEmptyState
            buttonText={firm.foundingYear === undefined ? 'Edit company' : 'Add report'}
            onButtonClick={
              firm.foundingYear === undefined
                ? () => setSidebar('editCompany')
                : openAddReportSidebar
            }
          />
        )}
      </section>
    </>
  );

  const financialsSection = (
    <>
      <ViewSectionHeader
        eyebrow="Investment"
        title="Financials"
        subtitle="Revenue, burn, profit (revenue − burn), cash on hand, and runway (cash ÷ burn) per report month."
      />
      <section className="financialSnapshot" aria-label="Financial figures from monthly reports">
        {companyReports.length === 0 ? (
          <GlobalEmptyState buttonText="Add report" onButtonClick={openAddReportSidebar} />
        ) : (
        <div className="table-card">
          <div className="table-scroll">
            <table className="data-table data-table--compact">
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Revenue</th>
                  <th>Burn</th>
                  <th>Profit</th>
                  <th>Cash</th>
                  <th>Runway</th>
                </tr>
              </thead>
              <tbody>
                {companyReports.map((r) => (
                    <tr key={r.id}>
                      <td>{formatMonthYear(r.periodMonth)}</td>
                      <td>{formatEuroOptional(r.revenue, false)}</td>
                      <td>{formatEuroOptional(r.burn, false)}</td>
                      <td className={monthlyProfitClassName(r.revenue, r.burn)}>
                        {monthlyProfitLabel(r.revenue, r.burn)}
                      </td>
                      <td>{formatEuroOptional(r.remainingCash, false)}</td>
                      <td>{formatRunway(computeRunwayMonths(r.remainingCash, r.burn))}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </section>
    </>
  );

  /** Primary buttons run the first analysis; secondary buttons regenerate an existing one. */
  function analyzeButtonIcon(variant: 'primary' | 'secondary', analyzing: boolean) {
    const useWhite = variant === 'primary' || isDarkMode;
    let src: string;
    if (analyzing) {
      src = useWhite
        ? '/assets/icons/fade.stagger.circles.animation.svg'
        : '/assets/icons/fade.stagger.circles.animation.black.svg';
    } else if (variant === 'primary') {
      src = '/assets/icons/apple.intelligence.svg';
    } else {
      src = isDarkMode ? '/assets/icons/repeat.svg' : '/assets/icons/repeat.black.svg';
    }
    return <img src={src} alt="" width={16} height={16} />;
  }

  const reportsSection = (
    <>
      <ViewSectionHeader
        eyebrow="Investment"
        title="Reports"
        subtitle="Monthly investor updates with revenue, burn, cash, and PDF attachments."
        actions={
          <GlobalButton
            type="button"
            variant="secondary"
            onClick={openAddReportSidebar}
            icon={
              <img
                src={isDarkMode ? '/assets/icons/plus.circle.svg' : '/assets/icons/plus.circle.black.svg'}
                alt=""
                width={16}
                height={16}
              />
            }
          >
            Add report
          </GlobalButton>
        }
      />
      <section className="monthlyReportsSection" aria-label="Monthly investor reports">
      {companyReports.length === 0 ? (
        <GlobalEmptyState buttonText="Add report" onButtonClick={openAddReportSidebar} />
      ) : (
        <ul className="monthlyReportList">
          {companyReports.map((r) => {
            const expanded = Boolean(expandedReportIds[r.id]);
            const hasAnalyzableSource = reportHasAnalyzableSource(r);
            const canAnalyze = reportCanAnalyze(r);
            const hasAnalysis = Boolean(r.updateIntelligence);
            const analysisIsStale = reportAnalysisIsStale(r);
            const analyzing = isAnalyzing('founderUpdate', r.id);
            const analyzeError = getJobError('founderUpdate', r.id);
            const rawText = r.rawText?.trim() ?? '';
            return (
            <li key={r.id} className="monthlyReportCard">
              <div className="monthlyReportCardTop">
                <button
                  type="button"
                  className="monthlyReportExpand"
                  onClick={() => toggleReportExpanded(r.id)}
                  aria-expanded={expanded}
                  aria-label={expanded ? 'Collapse report' : 'Expand report'}
                >
                  <span className="monthlyReportExpandIcon" aria-hidden>
                    {expanded ? '▼' : '▶'}
                  </span>
                  <span className="monthlyReportPeriod">{formatMonthYear(r.periodMonth)}</span>
                  {r.updateIntelligence ? (
                    <FounderUpdateSignalPills
                      sentiment={r.updateIntelligence.sentiment}
                      healthSignal={r.updateIntelligence.healthSignal}
                    />
                  ) : null}
                  {analysisIsStale ? (
                    <span className="verdictPill verdictPill--dont investmentCaseDetailStalePill">
                      Source changed
                    </span>
                  ) : null}
                </button>
                <div className="monthlyReportCardActions">
                  {hasAnalyzableSource ? (
                    <GlobalButton
                      type="button"
                      variant={hasAnalysis ? 'secondary' : 'primary'}
                      disabled={!canAnalyze || analyzing}
                      onClick={() => runFounderUpdateAnalysis(r)}
                      icon={analyzeButtonIcon(hasAnalysis ? 'secondary' : 'primary', analyzing)}
                    >
                      {analyzing
                        ? 'Analyzing…'
                        : hasAnalysis
                          ? 'Regenerate'
                          : 'Analyze update'}
                    </GlobalButton>
                  ) : null}
                  <GlobalButton
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setEditingReport(r);
                      setSidebar('editReport');
                    }}
                    icon={
                      <img
                        src={isDarkMode ? '/assets/icons/pencil.svg' : '/assets/icons/pencil.black.svg'}
                        alt=""
                        width={14}
                        height={14}
                      />
                    }
                  >
                    Edit
                  </GlobalButton>
                  <GlobalButton
                    type="button"
                    variant="secondary"
                    destructive
                    onClick={() => deleteMonthlyReport(r)}
                    icon={<img src="/assets/icons/trash.red.svg" alt="" width={14} height={14} />}
                  >
                    Delete
                  </GlobalButton>
                </div>
              </div>
              {expanded ? (
                <>
              {hasAnalyzableSource ? (
                <div className="investmentCaseDetailDeckAnalyze">
                  <p className="eyebrow">Update analysis</p>
                  {rawText.length > 0 && rawText.length < MIN_ANALYZE_CHARS && !r.pdfStoragePath ? (
                    <p className="muted">
                      Report text must be at least {MIN_ANALYZE_CHARS} characters before analyzing
                      (currently {rawText.length.toLocaleString()}).
                    </p>
                  ) : null}
                  <div className="investmentCaseDetailAnalyzeActions">
                    {!hasAnalysis ? (
                      <GlobalButton
                        type="button"
                        variant="primary"
                        disabled={!canAnalyze || analyzing}
                        onClick={() => runFounderUpdateAnalysis(r)}
                        icon={analyzeButtonIcon('primary', analyzing)}
                      >
                        {analyzing ? 'Analyzing…' : 'Analyze update'}
                      </GlobalButton>
                    ) : (
                      <GlobalButton
                        type="button"
                        variant="secondary"
                        disabled={!canAnalyze || analyzing}
                        onClick={() => runFounderUpdateAnalysis(r)}
                        icon={analyzeButtonIcon('secondary', analyzing)}
                      >
                        {analyzing ? 'Analyzing…' : 'Regenerate'}
                      </GlobalButton>
                    )}
                    {analysisIsStale ? (
                      <span className="verdictPill verdictPill--dont investmentCaseDetailStalePill">
                        Report text changed — re-analyze
                      </span>
                    ) : null}
                  </div>
                  {r.updateIntelligence ? (
                    <p className="muted">
                      Analyzed {new Date(r.updateIntelligence.analyzedAt).toLocaleString()} ·{' '}
                      {r.updateIntelligence.model}
                    </p>
                  ) : null}
                  {analyzeError ? (
                    <div className="investmentCaseDetailAnalyzeError">
                      <p className="sidebarFormError" role="alert">
                        {analyzeError}
                      </p>
                      <GlobalButton
                        type="button"
                        variant="secondary"
                        disabled={!canAnalyze || analyzing}
                        onClick={() => runFounderUpdateAnalysis(r)}
                      >
                        Retry
                      </GlobalButton>
                    </div>
                  ) : null}
                </div>
              ) : null}
              {r.updateIntelligence ? (
                <FounderUpdateIntelligenceSections intelligence={r.updateIntelligence} />
              ) : null}
              <div className="monthlyReportKpis">
                <div>
                  <span className="monthlyReportKpiLabel">Revenue</span>
                  <span className="monthlyReportKpiValue">{formatEuroOptional(r.revenue)}</span>
                </div>
                <div>
                  <span className="monthlyReportKpiLabel">Burn</span>
                  <span className="monthlyReportKpiValue">{formatEuroOptional(r.burn)}</span>
                </div>
                <div>
                  <span className="monthlyReportKpiLabel">Cash</span>
                  <span className="monthlyReportKpiValue">{formatEuroOptional(r.remainingCash)}</span>
                </div>
                <div>
                  <span className="monthlyReportKpiLabel">Runway</span>
                  <span className="monthlyReportKpiValue">
                    {formatRunway(computeRunwayMonths(r.remainingCash, r.burn))}
                  </span>
                </div>
                <div>
                  <span className="monthlyReportKpiLabel">PDF</span>
                  <span className="monthlyReportKpiValue">
                    {r.pdfUrl ? (
                      <a href={r.pdfUrl} target="_blank" rel="noopener noreferrer">
                        {r.originalPdfFileName || 'Open PDF'}
                      </a>
                    ) : (
                      '—'
                    )}
                  </span>
                </div>
              </div>
                  {r.rawText ? <pre className="monthlyReportRaw">{r.rawText}</pre> : null}
                </>
              ) : null}
            </li>
            );
          })}
        </ul>
      )}
      </section>
    </>
  );

  const notesSection = (
    <>
      <ViewSectionHeader
        eyebrow="Investment"
        title="Notes"
        subtitle="Forwarded emails and manual notes about this company."
        actions={
          <GlobalButton
            type="button"
            variant="secondary"
            onClick={openManualUpdateSidebar}
            icon={
              <img
                src={
                  isDarkMode
                    ? '/assets/icons/plus.circle.svg'
                    : '/assets/icons/plus.circle.black.svg'
                }
                alt=""
                width={16}
                height={16}
              />
            }
          >
            Add note
          </GlobalButton>
        }
      />
      <div className={styles.updatesPanel}>
        <CompanyUpdateTimeline
          companyId={firm.id}
          companyName={firm.name}
          updates={updates}
          companies={companies}
          unmatchedInboxItems={unmatchedInboxItems}
          userId={userId}
          setUpdates={setUpdates}
          setMonthlyUpdates={setMonthlyUpdates}
          onAddManualUpdate={openManualUpdateSidebar}
        />
      </div>
    </>
  );

  const chatSection = userId ? (
    <>
      <ViewSectionHeader
        eyebrow="Investment"
        title="Chat"
        subtitle={`Ask questions about ${firm.name} — grounded in its reports, notes, documents, and your Perfina data. Conversations are saved as separate threads.`}
      />
      <InvestmentChatPanel userId={userId} companyId={firm.id} companyName={firm.name} />
    </>
  ) : (
    <p className="muted">Sign in to chat about this investment.</p>
  );

  const sections: ViewSection[] = [
    { id: 'details', label: 'Details', content: detailsSection },
    { id: 'trend', label: 'Trend', content: trendSection },
    { id: 'financials', label: 'Financials', content: financialsSection },
    { id: 'reports', label: 'Reports', content: reportsSection },
    { id: 'notes', label: 'Notes', content: notesSection },
    {
      id: 'documents',
      label: 'Documents',
      content: userId ? (
        <>
          <ViewSectionHeader
            eyebrow="Investment"
            title="Documents"
            subtitle={`Files attached to ${firm.name} — indexed for intelligence search.`}
            actions={
              <GlobalButton
                type="button"
                variant="secondary"
                isLoading={documentUploadControl?.uploading}
                disabled={documentUploadControl?.uploading}
                onClick={() => documentUploadControl?.openFilePicker()}
                icon={
                  <img
                    src={
                      isDarkMode
                        ? '/assets/icons/icloud.and.arrow.up.svg'
                        : '/assets/icons/icloud.and.arrow.up.black.svg'
                    }
                    alt=""
                    width={16}
                    height={16}
                  />
                }
              >
                Upload document
              </GlobalButton>
            }
          />
          <EntityDocumentsPanel
            userId={userId}
            entityType="investment"
            entityId={firm.id}
            entityLabel={firm.name}
            onUploadControlChange={setDocumentUploadControl}
          />
        </>
      ) : (
        <p className="muted">Sign in to manage documents.</p>
      ),
    },
    { id: 'chat', label: 'Chat', content: chatSection },
  ];

  const detailActions = (
    <>
      <ViewLayoutToggle mode={layoutMode} onToggle={toggleLayout} />
      <GlobalButton
        type="button"
        variant="secondary"
        onClick={() => setSidebar('editCompany')}
        icon={
          <img
            src={isDarkMode ? '/assets/icons/pencil.svg' : '/assets/icons/pencil.black.svg'}
            alt=""
            width={16}
            height={16}
          />
        }
      >
        Edit company
      </GlobalButton>
      <GlobalButton
        type="button"
        variant="secondary"
        destructive
        onClick={deleteCompany}
        icon={<img src="/assets/icons/trash.red.svg" alt="" width={16} height={16} />}
      >
        Delete company
      </GlobalButton>
    </>
  );

  return (
    <ViewPage
      title={firm.name}
      backTo="/investments"
      backLabel="Back to investments"
      actions={detailActions}
    >
      <div className={styles.root}>
        <ViewSections mode={layoutMode} sections={sections} tabAliases={{ updates: 'notes' }} />

      <GlobalSidebar isOpen={sidebar === 'editCompany'} onClose={closeSidebar}>
        <div className="sidebarFormStack">
          <h2 className="sidebarFormTitle">Edit company</h2>
          <InvestmentCompanyForm
            company={firm}
            onSubmit={saveCompany}
            onCancel={closeSidebar}
            onDelete={deleteCompany}
          />
        </div>
      </GlobalSidebar>

      <GlobalSidebar isOpen={sidebar === 'addReport' || sidebar === 'editReport'} onClose={closeReportSidebar}>
        <div className="sidebarFormStack">
          <h2 className="sidebarFormTitle">{sidebar === 'editReport' ? 'Edit report' : 'New report'}</h2>
          {userId ? (
            <MonthlyUpdateForm
              key={editingReport?.id ?? 'new'}
              companyId={firm.id}
              userId={userId}
              report={editingReport ?? undefined}
              onSubmit={saveMonthlyReport}
              onCancel={closeReportSidebar}
              onDelete={editingReport ? () => deleteMonthlyReport(editingReport) : undefined}
            />
          ) : (
            <p className="muted">You must be signed in to save reports.</p>
          )}
        </div>
      </GlobalSidebar>

      <GlobalSidebar
        isOpen={sidebar === 'addUpdate' || sidebar === 'editUpdate'}
        onClose={closeUpdateSidebar}
      >
        <div className="sidebarFormStack">
          <h2 className="sidebarFormTitle">
            {sidebar === 'editUpdate' ? 'Edit note' : 'New note'}
          </h2>
          <CompanyUpdateForm
            key={editingUpdate?.id ?? 'new'}
            companyId={firm.id}
            update={editingUpdate ?? undefined}
            onSubmit={saveCompanyUpdate}
            onCancel={closeUpdateSidebar}
            onDelete={editingUpdate ? () => deleteCompanyUpdate(editingUpdate) : undefined}
          />
        </div>
      </GlobalSidebar>
      </div>
    </ViewPage>
  );
}
